package com.arena_web.data.repository

import com.arena_web.data.http.ApiResponse
import com.arena_web.data.http.isNetworkError
import io.ktor.http.*
import kotlinx.coroutines.CancellationException

/**
 * One transparent retry for repeatable uploads: a flaky mobile uplink drops
 * long photo uploads far more often than short API calls, and an in-memory
 * byte-array body is safe to resend. Only for calls that don't create
 * records — retrying e.g. a chat media message could duplicate it.
 */
suspend fun <T> retryOnceOnNetworkError(block: suspend () -> T): T =
    try {
        block()
    } catch (e: Throwable) {
        if (e is CancellationException || !e.isNetworkError()) throw e
        block()
    }

suspend fun <T> apiCall(block: suspend () -> ApiResponse<T>): Result<T> {
    return try {
        val response = block()
        if (response.data != null) {
            Result.success(response.data)
        } else if (response.errors.isNullOrEmpty() && response.code in 200..299) {
            // Successful response with empty body (e.g. 200/201/204 with no data)
            @Suppress("UNCHECKED_CAST")
            Result.success(Unit as T)
        } else {
            val errorMsg = response.errors?.joinToString() ?: "Request failed"
            Result.failure(ApiCallException(response.code, errorMsg))
        }
    } catch (e: Exception) {
        Result.failure(e)
    }
}

suspend fun apiCallUnit(block: suspend () -> ApiResponse<Unit>): Result<Unit> {
    return try {
        val response = block()
        if (response.errors.isNullOrEmpty()) {
            Result.success(Unit)
        } else {
            Result.failure(Exception(response.errors.joinToString()))
        }
    } catch (e: Exception) {
        Result.failure(e)
    }
}

/**
 * Failure carrying the HTTP status reported in the [ApiResponse] (the backend's
 * StatusPages echoes the status into `code`; safeBody does the same for
 * 401/403). Message is unchanged, so existing `e.message` handling still works.
 */
class ApiCallException(val statusCode: Int, message: String) : Exception(message)

class HttpStatusException(val statusCode: HttpStatusCode) :
    Exception("Request failed: $statusCode")

suspend fun apiCallStatus(block: suspend () -> HttpStatusCode): Result<Unit> {
    return try {
        val status = block()
        if (status.isSuccess()) Result.success(Unit)
        else Result.failure(HttpStatusException(status))
    } catch (e: Exception) {
        Result.failure(e)
    }
}
